import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Col, Row } from 'antd';
import { omitBy } from 'lodash';

import FormItem, { TYPE_INPUT } from 'components/FormItem';

import RefreshIcon from 'resources/svg/refresh_icon.svg';

import { useChangeAddress } from 'hooks/useChangeAddress';
import { ORDERS } from 'utils';
import { NFTParamsTypes } from 'pages/nft-management';
import LENGTH_CONSTANTS from 'constants/length';
import { NFT_MANAGEMENT_FIELD } from 'constants/nft';
import { DURATION, POOL_STATUS, ROLE, ROLE_STATUS } from 'constants/common';

const { KEYWORD, PAGE } = NFT_MANAGEMENT_FIELD;
const { DEFAULT_PAGE } = LENGTH_CONSTANTS;
const { FIELD, ORDER } = ORDERS;

type SearchFormProps = {
  onSubmit: (values: any) => void;
  params: NFTParamsTypes;
};

const initialValues = {
  keyword: '',
  status: null,
  duration: null,
};

const SearchForm = ({ onSubmit, params }: SearchFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<any>(null);

  const formatStatusOptions = [
    {
      name: POOL_STATUS.DRAFT.text,
      value: POOL_STATUS.DRAFT.value,
    },
    {
      name: POOL_STATUS.WAITING.text,
      value: POOL_STATUS.WAITING.value,
    },
    {
      name: POOL_STATUS.LIVE.text,
      value: POOL_STATUS.LIVE.value,
    },
    {
      name: POOL_STATUS.FINISHED.text,
      value: POOL_STATUS.FINISHED.value,
    },
  ];
  const duration = DURATION;

  const handleChangeField =
    (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, field: string) =>
    (value: string) => {
      setFieldValue(field, value?.trim());
      onSubmit({
        ...params,
        [field]: value?.trim(),
        [PAGE]: DEFAULT_PAGE,
      });
    };

  const handleResetForm = () => {
    formRef?.current?.resetForm();
    onSubmit({
      ...omitBy(params, [FIELD, ORDER]),
      ...initialValues,
      [PAGE]: DEFAULT_PAGE,
    });
  };

  useChangeAddress({ callback: handleResetForm });

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} innerRef={formRef}>
      {({ setFieldValue }) => (
        <Form className="search-form nft-management-search-form">
          <Row gutter={20} className="nft-management-search-form__row">
            <Col lg={8} sm={12} xs={24}>
              <FormItem
                name={KEYWORD}
                className="search-form__name--input"
                placeholder={t('pool_management.txt_search_placeholder')}
                onSearch={handleChangeField(setFieldValue, KEYWORD)}
                typeInput={TYPE_INPUT.SEARCH}
              />
            </Col>
            <Col lg={3} sm={12} xs={24}>
              <FormItem
                options={formatStatusOptions}
                name="status"
                onChange={handleChangeField(setFieldValue, 'status')}
                typeInput={TYPE_INPUT.SELECT}
                defaultValue=""
                allowClear
                placeholder="Pool Status"
              />
            </Col>
            <Col lg={3} sm={12} xs={24}>
              <FormItem
                options={duration}
                name="duration"
                onChange={handleChangeField(setFieldValue, 'duration')}
                typeInput={TYPE_INPUT.SELECT}
                defaultValue=""
                allowClear
                placeholder="Duration Lock"
              />
            </Col>
            <Col lg={10} sm={24} xs={24} className="nft-management-search-form__group-button">
              <img src={RefreshIcon} onClick={handleResetForm} className="cursor-pointer" />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default SearchForm;
